import { Injectable } from '@angular/core';
import { BaseVariablePropertyFactoryService, BaseVariableService, VariableCrudManagerService, getDeviceOperations, VARIABLE_CONSTANTS } from '@wms/variables';
import { clone, cloneDeep, difference, filter, find, findIndex, flatten, forEach, isUndefined, keys, map, reject, sortBy, uniq } from 'lodash-es';
import { BaseComponent } from '@wms/components';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class WmsDeviceVariableService extends BaseComponent {

  serviceOptions: any = {};
  allProperties: any = [];
  propertyChangeHandlers: any = {
    service: (variable: any) => {
      this.prepareServicesList(variable);
    },
    operation: (variable: any) => {
      this.prepareOperationsList(variable);
    }
  };

  private availableServices: any = {};
  private propertyGroups: any;
  private propertyGroupChangeSubject = new Subject();

  constructor(private variableService: BaseVariableService, private variableCrudManagerService: VariableCrudManagerService, private propertyFactory: BaseVariablePropertyFactoryService) {
    super();
    this.propertyGroups = this.propertyFactory.getPropertyGroups();
    const deviceOperations = getDeviceOperations();
    forEach(deviceOperations, (operation: any, service: any) => {
      forEach(operation, (value: any, key: any) => {
        this.addOperation(service, key, value);
      });
    });
  }

  __getVariableDependencies() {
    this.allProperties = this.listAllProperties();
    forEach(this.listServices(), (serviceName: any) => {
      this.serviceOptions[serviceName] = serviceName;
    });
    // if ($rootScope.hasSpotcuesSupport) {
    //     delete serviceOptions.datasync;
    // }
    this.variableService.selectedVariable.propertyObj.service.options = this.serviceOptions;
    this.__onPropertyChange('service', undefined, true);
  }

  __onPropertyChange = (propertyName: any, value: any, init?: any) => {
    if (!init) {
      this.variableService.markUpdated();
    }
    if (VARIABLE_CONSTANTS.EVENTS.indexOf(propertyName) > -1) {
      this.variableService.onEventChange(propertyName, value);
    } else if (this.propertyChangeHandlers[propertyName]) {
      this.propertyChangeHandlers[propertyName](this.variableService.selectedVariable, init);
    }
  };

  prepareServicesList = (variable: any) => {
    let operationProperty = variable.propertyObj.operation,
      service = variable.variableObject.service,
      allowedOperations: any = this.listAllOperations(service),
      allowedOperationsOptions: any = {},
      selectedOperation = variable.variableObject.operation;
    forEach(allowedOperations, (value: any) => {
      allowedOperationsOptions[value] = value;
    });
    //reset operation property options
    if (allowedOperations.indexOf(selectedOperation) >= 0) {
      operationProperty.value = selectedOperation;
    } else {
      operationProperty.value = allowedOperations[0];
      variable.variableObject.operation = allowedOperations[0];
    }
    // if ($rootScope.hasSpotcuesSupport && service === 'device') {
    //   delete allowedOperationsOptions.goOffline;
    //   delete allowedOperationsOptions.goOnline;
    //   delete allowedOperationsOptions.getAppInfo;
    //   variable.variableObject.operation = 'getDeviceInfo';
    // }
    operationProperty.hide = false;
    operationProperty.options = allowedOperationsOptions;
    this.prepareOperationsList(variable);
  }

  prepareOperationsList = (variable: any) => {
    let service = variable.variableObject.service,
      operation = variable.variableObject.operation,
      operationDef: any = this.getOperation(service, operation),
      operationPropertyObjects = operationDef.properties,
      bindingPropertyObjects = filter(operationPropertyObjects, { dataBinding: true }),
      operationProperties = map(reject(operationDef.properties, { dataBinding: true }), "target"),
      nonRequiredProperties = difference(this.allProperties, operationProperties);
    //Show only required properties and hide remaining properties.
    forEach(nonRequiredProperties, (property: any) => {
      if (variable.propertyObj[property]) {
        variable.propertyObj[property].hide = true;
      }
      delete variable.variableObject[property];
      delete variable.variableObject.dataBinding[property];
    });
    // Except data bindable properties, clone rest of those properties and set model objects.
    forEach(operationPropertyObjects, (property: any) => {
      let cloneProperty;
      if (!property.dataBinding && !variable.propertyObj[property.target]) {
        cloneProperty = clone(property);
        cloneProperty.modelObj = variable.variableObject;
        cloneProperty.modelKey = property.target;
        variable.propertyObj[property.target] = cloneProperty;
      }
    });
    forEach(operationProperties, (property: any) => {
      if (variable.propertyObj[property]) {
        variable.propertyObj[property].hide = false;
      }
    });
    forEach(operationPropertyObjects, (property: any) => {
      let propertyObj = variable.propertyObj[property.target];
      if (propertyObj) {
        if (property.hide !== undefined) {
          propertyObj.hide = property.hide;
        }
        if (property.value !== undefined) {
          propertyObj.value = property.value;
          if (isUndefined(propertyObj.modelObj[propertyObj.modelKey])) {
            propertyObj.modelObj[propertyObj.modelKey] = property.value;
          }
        }
      }
    });

    if (variable.propertyObj.spinnerContext) {
      variable.propertyObj.spinnerContext.options = this.variableCrudManagerService.getSpinnerWidgets(variable.variableObject);
    }

    variable.variableObject.dataSet = cloneDeep(operationDef.model);

    /* prepare bindings(merge existing) in dataBinding list and add them to properties object for UI */
    this.variableService.prepareBindings(cloneDeep(bindingPropertyObjects));
    this.variableService.addBindingsToProperties();
  }

  addOperation = (serviceName: any, operationName: any, serviceInfo: any) => {
    this.availableServices[serviceName] = this.availableServices[serviceName] || {};
    this.availableServices[serviceName][operationName] = serviceInfo;
    forEach(serviceInfo.properties, this.addPropertyToGroup);
  };

  listServices = () => {
    return sortBy(keys(this.availableServices));
  };

  getOperation = (serviceName: any, operationName: any) => {
    let operation = {};
    if (serviceName && operationName) {
      operation = this.availableServices[serviceName][operationName] || {};
    }
    return operation;
  }

  listAllOperations = (serviceName: any) => {
    return sortBy(keys(this.availableServices[serviceName]));
  };

  listAllProperties = () => {
    let allProperties: any = [],
      properties = [];
    forEach(this.availableServices, (service: any) => {
      forEach(service, (api: any) => {
        properties = map(reject(api.properties, { dataBinding: true }), 'target');
        allProperties.push(properties);
      });
    });
    return uniq(flatten(allProperties));
  };

  addPropertyToGroup = (propertyDef: any) => {
    if (propertyDef.group && propertyDef.subGroup) {
      let targetSubGroup = find(this.propertyGroups, { "name": propertyDef.subGroup, "parent": propertyDef.group });
      if (targetSubGroup && findIndex(targetSubGroup.properties, propertyDef.target) < 0) {
        targetSubGroup.properties.push(propertyDef.target);
      }
    }
  }
}
